/**
 * ============================================================================
 * MembershipsNew - PRODUCTION-READY MEMBERSHIPS PAGE
 * ============================================================================
 *
 * Production-ready membership management with:
 * - Packages tab: CRUD for membership packages
 * - Members tab: View purchased memberships, stats, and member details
 * - Member detail drawer with renewal functionality
 * - Toast notifications for success/error feedback
 * - TanStack Query for data fetching
 * - 100% type safety
 *
 * ============================================================================
 */

import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, RefreshCw, AlertTriangle, Calendar, Download, Plus } from 'lucide-react';
import { useMembershipsData } from '../hooks/useMembershipsData';
import { useMembershipMutations } from '../hooks/useMembershipMutations';
import { useMembersData } from '../hooks/useMembersData';
import { useMemberMutations } from '../hooks/useMemberMutations';
import { vendorApi } from '../services/api';
import { MembershipStats } from '../components/memberships/MembershipStats';
import { MembershipsTable } from '../components/memberships/MembershipsTable';
import { MembershipFormModal } from '../components/memberships/MembershipFormModal';
import { MemberStats } from '../components/memberships/MemberStats';
import { MembersTable } from '../components/memberships/MembersTable';
import { AddManualMemberModal } from '../components/memberships/AddManualMemberModal';
import { MemberDetailDrawer } from '../components/memberships/MemberDetailDrawer';
import Toast from '../components/Toast';
import Modal from '../components/ui/Modal';
import type {
  MembershipFilters,
  MembershipPackage,
  MemberFilters,
  ManualMemberFormData,
  Member,
  RenewalFormData,
  CancellationFormData,
} from '../types/memberships';
import type { MembershipFormData } from '../components/memberships/MembershipFormModal';

type MainTab = 'packages' | 'members';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

export default function MembershipsNew() {
  const { user } = useAuth();
  const venue = user?.venues?.[0];
  const queryClient = useQueryClient();

  // ============================================================================
  // STATE
  // ============================================================================

  const [mainTab, setMainTab] = useState<MainTab>('packages');

  // Package filters
  const [filters, setFilters] = useState<MembershipFilters>({
    search: '',
    status: 'all',
    durationType: 'all',
  });

  // Member filters
  const [memberFilters, setMemberFilters] = useState<MemberFilters>({
    status: 'all',
    search: '',
    sort: 'recent',
    page: 1,
  });

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<MembershipPackage | null>(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState<number | null>(null);

  // Member detail drawer state
  const [selectedMemberUserId, setSelectedMemberUserId] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<ToastState | null>(null);

  // Duplicate membership warning state
  const [duplicateWarning, setDuplicateWarning] = useState<{
    memberName: string;
    existingMemberships: Array<{ package_name: string; end_date: string; days_remaining: number }>;
    formData: ManualMemberFormData;
  } | null>(null);

  // Export members confirmation state
  const [showExportConfirmation, setShowExportConfirmation] = useState(false);

  // ============================================================================
  // TOAST HELPER
  // ============================================================================

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  // ============================================================================
  // DATA FETCHING (fetch both datasets on mount for instant tab switching)
  // ============================================================================

  const {
    data: packagesData,
    isPending: packagesLoading,
    error: packagesError,
    refetch: refetchPackages,
  } = useMembershipsData(venue?.slug, filters);

  const {
    data: membersData,
    isPending: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useMembersData(venue?.slug, memberFilters);

  const { addPackage, updatePackage, deletePackage, toggleStatus } =
    useMembershipMutations(venue?.slug);

  // Member mutations with toast callbacks
  const { addManualMember, sendReminder, renewMembership, cancelMembership } = useMemberMutations(venue?.slug, {
    onSuccess: (message) => showToast(message, 'success'),
    onError: (message) => showToast(message, 'error'),
  });

  // ============================================================================
  // HANDLERS - Packages
  // ============================================================================

  const handleFilterChange = (newFilters: Partial<MembershipFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleAddPackage = () => {
    setEditingPackage(null);
    setIsModalOpen(true);
  };

  const handleEditPackage = (pkg: MembershipPackage) => {
    setEditingPackage(pkg);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPackage(null);
  };

  const handleFormSubmit = async (formData: MembershipFormData) => {
    try {
      if (editingPackage) {
        await updatePackage.mutateAsync({ packageId: editingPackage.id, formData });
        showToast('Package updated successfully', 'success');
      } else {
        await addPackage.mutateAsync(formData);
        showToast('Package created successfully', 'success');
      }
      handleCloseModal();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save package', 'error');
    }
  };

  const handleDeletePackage = async (id: number) => {
    try {
      await deletePackage.mutateAsync(id);
      showToast('Package deleted successfully', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to delete package', 'error');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await toggleStatus.mutateAsync(id);
      showToast('Package status updated', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to toggle status', 'error');
    }
  };

  // ============================================================================
  // HANDLERS - Members
  // ============================================================================

  const handleMemberFilterChange = (newFilters: Partial<MemberFilters>) => {
    setMemberFilters((prev) => {
      // Reset to page 1 if status or sort changes (not for pagination itself)
      const shouldResetPage = (newFilters.status !== undefined && newFilters.status !== prev.status) ||
                             (newFilters.sort !== undefined && newFilters.sort !== prev.sort);

      return {
        ...prev,
        ...newFilters,
        ...(shouldResetPage && !newFilters.page ? { page: 1 } : {}),
      };
    });
  };

  // Prefetch member details on hover for instant modal opening
  const handleMemberHover = useCallback((userId: number) => {
    if (venue?.slug) {
      queryClient.prefetchQuery({
        queryKey: ['member-detail', venue.slug, userId],
        queryFn: () => vendorApi.getMemberDetail(venue.slug, userId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    }
  }, [venue?.slug, queryClient]);

  const handleAddMemberSubmit = async (formData: ManualMemberFormData & { force?: boolean }) => {
    try {
      const result = await addManualMember.mutateAsync(formData);

      // Check if backend returned a warning about duplicate membership
      if (result.status === 'warning' && result.data) {
        setDuplicateWarning({
          memberName: result.data.member_name,
          existingMemberships: result.data.existing_memberships,
          formData: formData,
        });
        return; // Don't close modal, show warning dialog
      }

      // Success - close modal
      setIsAddMemberOpen(false);
      // Toast is handled by mutation callback
    } catch {
      // Error toast is handled by mutation callback
    }
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateWarning) return;

    // Retry with force flag
    try {
      await addManualMember.mutateAsync({ ...duplicateWarning.formData, force: true });
      setDuplicateWarning(null);
      setIsAddMemberOpen(false);
      showToast('Member added successfully', 'success');
    } catch {
      // Error toast is handled by mutation callback
      setDuplicateWarning(null);
    }
  };

  const handleSendReminder = async (membershipId: number) => {
    setSendingReminderId(membershipId);
    try {
      await sendReminder.mutateAsync(membershipId);
      // Toast is handled by mutation callback
    } catch {
      // Error toast is handled by mutation callback
    } finally {
      setSendingReminderId(null);
    }
  };

  const handleMemberClick = (member: Member) => {
    setSelectedMemberUserId(member.user.id);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    // Delay clearing userId to allow animation to complete
    setTimeout(() => setSelectedMemberUserId(null), 300);
  };

  const handleRenewMembership = async (userId: number, formData: RenewalFormData) => {
    try {
      await renewMembership.mutateAsync({ userId, formData });
    } catch {
      // Error toast handled by mutation onError callback
    }
  };

  const handleCancelMembership = async (membershipId: number, formData: CancellationFormData) => {
    try {
      await cancelMembership.mutateAsync({ membershipId, formData });
    } catch {
      // Error toast handled by mutation onError callback
    }
  };

  // ============================================================================
  // LOADING / ERROR / NO VENUE
  // ============================================================================

  const isPending = mainTab === 'packages' ? packagesLoading : membersLoading;
  const error = mainTab === 'packages' ? packagesError : membersError;
  const refetch = mainTab === 'packages' ? refetchPackages : refetchMembers;

  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Memberships</h1>
          <p className="text-gray-600 text-sm">Loading memberships...</p>
        </div>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <RefreshCw className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Memberships</h1>
          <p className="text-gray-600 text-sm">Error loading memberships</p>
        </div>
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-500 text-sm mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // EXPORT HANDLERS
  // ============================================================================

  const handleExportPackages = () => {
    const packages = packagesData?.packages || [];
    if (packages.length === 0) {
      showToast('No packages available to export', 'info');
      return;
    }

    const escapeCSV = (value: string | number | undefined | null): string => {
      if (value === undefined || value === null) return '""';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvData = packages.map((pkg) => ({
      Name: pkg.name,
      Price: pkg.price,
      Duration: `${pkg.duration} ${pkg.duration_type}${pkg.duration > 1 ? 's' : ''}`,
      Members: pkg.members_count || 0,
      Revenue: pkg.total_revenue || 0,
      Status: pkg.status,
    }));

    const headers = Object.keys(csvData[0] || {}).map(escapeCSV).join(',');
    const rows = csvData.map((row) => Object.values(row).map(escapeCSV).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packages-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportMembers = () => {
    // Show confirmation modal before exporting member data
    setShowExportConfirmation(true);
  };

  const actuallyExportMembers = () => {
    const members = membersData?.members || [];
    if (members.length === 0) {
      showToast('No members available to export', 'info');
      return;
    }

    const escapeCSV = (value: string | number | undefined | null): string => {
      if (value === undefined || value === null) return '""';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvData = members.map((member) => ({
      Name: member.user?.name || '',
      Email: member.user?.email || '',
      Phone: member.user?.phone || '',
      Package: member.package?.package_name || '',
      'Start Date': member.start_date || '',
      'End Date': member.end_date || '',
      'Days Remaining': member.days_remaining || 0,
      Price: member.price || 0,
      Status: member.is_active ? 'Active' : 'Expired',
    }));

    const headers = Object.keys(csvData[0] || {}).map(escapeCSV).join(',');
    const rows = csvData.map((row) => Object.values(row).map(escapeCSV).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setShowExportConfirmation(false);
    showToast('Members exported successfully', 'success');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header with Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Memberships</h1>
          <p className="text-gray-600 text-sm">
            {mainTab === 'packages'
              ? `${packagesData?.packages.length || 0} ${(packagesData?.packages.length || 0) === 1 ? 'package' : 'packages'} found`
              : `${membersData?.members.length || 0} ${(membersData?.members.length || 0) === 1 ? 'member' : 'members'} found`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={mainTab === 'packages' ? handleExportPackages : handleExportMembers}
            title={mainTab === 'members' ? 'Export contains customer data. Handle with care and in compliance with data protection regulations.' : 'Export packages to CSV'}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Export</span>
            <span className="sm:hidden">CSV</span>
          </button>
          <button
            onClick={mainTab === 'packages' ? handleAddPackage : () => setIsAddMemberOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">
              {mainTab === 'packages' ? 'Add Package' : 'Add Member'}
            </span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {mainTab === 'packages' ? (
        <MembershipStats
          totalPackages={packagesData?.stats.totalPackages || 0}
          activePackages={packagesData?.stats.activePackages || 0}
          totalMembers={packagesData?.stats.totalMembers || 0}
          monthlyRevenue={packagesData?.stats.monthlyRevenue || 0}
        />
      ) : (
        <MemberStats
          stats={membersData?.stats || {
            active_members: 0,
            total_members: 0,
            expiring_soon: 0,
            total_revenue: 0,
            venue_earnings: 0,
          }}
        />
      )}

      {/* Content - Packages or Members */}
      {mainTab === 'packages' ? (
        <>
          {/* Packages Table */}
          <MembershipsTable
            packages={packagesData?.packages || []}
            filters={filters}
            onFilterChange={handleFilterChange}
            onAddPackage={handleAddPackage}
            onEditPackage={handleEditPackage}
            onDeletePackage={handleDeletePackage}
            onToggleStatus={handleToggleStatus}
            onSwitchToMembers={() => setMainTab('members')}
          />

          {/* Package Form Modal */}
          <MembershipFormModal
            isOpen={isModalOpen}
            package={editingPackage}
            onClose={handleCloseModal}
            onSubmit={handleFormSubmit}
            isSaving={addPackage.isPending || updatePackage.isPending}
          />
        </>
      ) : (
        <>
          {/* Members Table */}
          <MembersTable
            members={membersData?.members || []}
            filters={memberFilters}
            pagination={membersData?.pagination || { current_page: 1, last_page: 1, per_page: 20, total: 0 }}
            onFilterChange={handleMemberFilterChange}
            onAddMember={() => setIsAddMemberOpen(true)}
            onMemberClick={handleMemberClick}
            onMemberHover={handleMemberHover}
            onSendReminder={handleSendReminder}
            sendingReminderId={sendingReminderId}
            onSwitchToPackages={() => setMainTab('packages')}
          />

          {/* Add Member Modal */}
          <AddManualMemberModal
            isOpen={isAddMemberOpen}
            venueId={venue.id}
            packages={membersData?.packages || []}
            onClose={() => setIsAddMemberOpen(false)}
            onSubmit={handleAddMemberSubmit}
            isSaving={addManualMember.isPending}
          />

          {/* Member Detail Drawer */}
          <MemberDetailDrawer
            isOpen={isDrawerOpen}
            userId={selectedMemberUserId}
            venueSlug={venue.slug}
            packages={membersData?.packages || []}
            onClose={handleDrawerClose}
            onRenew={handleRenewMembership}
            isRenewing={renewMembership.isPending}
            onCancel={handleCancelMembership}
            isCancelling={cancelMembership.isPending}
          />

          {/* Duplicate Membership Warning Dialog */}
          {duplicateWarning && (
            <Modal
              isOpen={true}
              onClose={() => setDuplicateWarning(null)}
              size="md"
              preventClose={addManualMember.isPending}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">Duplicate Membership Detected</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">{duplicateWarning.memberName}</span> already has active memberships at this venue.
                    </p>
                  </div>
                </div>

                {/* Existing Memberships List */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                  <p className="text-xs font-semibold text-amber-900 uppercase mb-3">Existing Active Memberships:</p>
                  <div className="space-y-2">
                    {duplicateWarning.existingMemberships.map((membership, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-semibold text-gray-900">{membership.package_name}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Expires {new Date(membership.end_date).toLocaleDateString()}</p>
                          <p className="text-xs font-semibold text-amber-600">{membership.days_remaining} days left</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">Note:</span> Users can have multiple concurrent memberships (e.g., Basketball + Football).
                    Only add another membership if this is intentional.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setDuplicateWarning(null)}
                    disabled={addManualMember.isPending}
                    className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDuplicateConfirm}
                    disabled={addManualMember.isPending}
                    className="flex-1 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addManualMember.isPending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Anyway'
                    )}
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Export Members Confirmation Dialog */}
          <Modal
            isOpen={showExportConfirmation}
            onClose={() => setShowExportConfirmation(false)}
            size="sm"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">Export Member Data</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    You're about to export customer information
                  </p>
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                <p className="text-sm text-amber-900">
                  <span className="font-semibold">⚠️ Export contains customer data.</span>
                  <br />
                  Handle responsibly and in compliance with data protection regulations.
                </p>
              </div>

              {/* Info Message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                <p className="text-xs text-blue-700">
                  The export will include: member names, emails, phone numbers, package details, and membership status.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowExportConfirmation(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={actuallyExportMembers}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
